import { useEffect } from "react";
import { Col, FormFeedback, FormGroup, Input, Label, Row } from "reactstrap";
import useValueList, { getValueInfo } from "../../../hook/useValueList";
import { ContainerLayoutProps } from "../../../types/common";
import { Action } from "../../../types/reducer-action";
import { DataEntryEvent, FormErrorAction, FormUpdateAction } from "../../common/data-entry-reducer";
import InlineFeedback from "../../common/inline-feedback";
import CityInput from "../../common/search-input/city-input";
import PostalCodeInput from "../../common/search-input/postal-code-input";
import { useFrench } from "../../french/french-context";
import { useAddressDispatch, useAddressState } from "./address-manager-context";
import './style.scss';

const STREET_NUMBER_SURFFIXES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', '1/2', '1/4', '3/4'];

const AddressInfoPanel = (props: ContainerLayoutProps) => {
  const { wide, autoFocus } = props;

  const colWidth = wide ? 6 : 12;

  const [streetTypes] = useValueList('STREET_TYPE_CODE');
  const [streetDirections] = useValueList('STREET_DIRECT_CODE');

  const [cities] = useValueList('CITY');
  const [provinces] = useValueList('PROVINCE');
  const [countries] = useValueList('COUNTRY');

  const { form, errors } = useAddressState();

  const dispatch = useAddressDispatch();

  const [frenchState] = useFrench();

  useEffect(() => {
    // auto select country based on the given province
    if (form?.provinceId && provinces.length > 0) {
      const province = getValueInfo(provinces, form.provinceId);
      if (province?.parent) {
        dispatch(new Action(DataEntryEvent.UpdateForm,
          { countryId: province.parent.id }
        ));
      }
    }
  }, [provinces, form.provinceId, dispatch]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(new FormUpdateAction(event));

    if (event.target.name === 'buildingName') {
      dispatch(new Action(DataEntryEvent.UpdateForm,
        { buildingNameFrenchSync: 0 }
      ));
    }
  };

  const handleSelectMunicipality = (id?: number) => {
    const city = getValueInfo(cities, id);

    dispatch(new Action(DataEntryEvent.UpdateForm,
      {
        municipalityId: id,
        provinceId: city?.parent?.id
      }
    ));
  };

  const handleSelectPostalCode = (id?: number, postalCode?: string) => {
    dispatch(new Action(DataEntryEvent.UpdateForm,
      {
        postalCodeId: id,
        postalCode: postalCode
      }
    ));
  };

  const handleInvalid = (event: React.InvalidEvent<HTMLInputElement>) => {
    event.preventDefault();

    const { name, validity } = event.target;

    if (name === 'civicNumber' && validity.patternMismatch) {
      dispatch(new Action(DataEntryEvent.AddErrors,
        { [name]: "Only numbers are allowed. A dash can be used in between." }
      ));
    } else {
      dispatch(new FormErrorAction(event));
    }
  };

  return (
    <section>
      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="civic_number" sm={4}>Street Number:</Label>
            <Col sm={8}>
              <Input 
                id="civic_number" 
                name="civicNumber"
                aria-label="Street Number"
                placeholder="Enter a street number" 
                maxLength={100}
                value={form?.civicNumber ?? ''} 
                onChange={handleChange}
                invalid={!!errors.civicNumber} 
                onInvalid={handleInvalid}
                autoFocus={autoFocus}
                pattern="^\d+(-\d+)?$" />
              <FormFeedback>{errors.civicNumber}</FormFeedback>
            </Col>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="civic_number_suffix" sm={4}>Street Number Suffix:</Label>
            <Col sm={8}>
              <Input type="select" id="civic_number_suffix" name="civicNumberSuffix"
                value={form?.civicNumberSuffix ?? ''} onChange={handleChange}>
                <option value="" disabled>Select a street number suffix</option>
                {STREET_NUMBER_SURFFIXES.map(s =>
                  <option key={s} value={s}>{s}</option>
                )}
              </Input>
            </Col>
          </FormGroup>
        </Col>
      </Row>

      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="street_name" sm={4}>Street Name:</Label>
            <Col sm={8}>
              <Input id="street_name" name="streetName"
                placeholder="Enter a street name" maxLength={100}
                value={form?.streetName ?? ''} onChange={handleChange} />
            </Col>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="street_type" sm={4}>Street Type:</Label>
            <Col sm={8}>
              <Input type="select" id="street_type" name="streetTypeCodeId"
                value={form?.streetTypeCodeId ?? 0} onChange={handleChange}>
                <option value="0" disabled>Select a street type</option>
                {streetTypes?.filter(item => item.lang == null || item.lang !== 'FR').map(item =>
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                )}
              </Input>
            </Col>
          </FormGroup>
        </Col>
      </Row>

      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="street_direction" sm={4}>Street Direction:</Label>
            <Col sm={8}>
              <Input type="select" id="street_direction" name="streetDirCodeId"
                value={form?.streetDirCodeId ?? 0} onChange={handleChange}>
                <option value="0" disabled>Select a street direction</option>
                {streetDirections?.filter(item => item.lang == null || item.lang !== 'FR').map(item =>
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                )}
              </Input>
            </Col>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="building_name" sm={4}>Building Name:</Label>
            <Col sm={8}>
              <Input id="building_name" name="buildingName"
                placeholder="Enter a building name" maxLength={100}
                value={form?.buildingName ?? ''} onChange={handleChange} />
            </Col>
          </FormGroup>
          {
            frenchState.isFrenchChecked && <>

              <FormGroup row>
                <Label for="french_building_name" sm={4}>Building Name (French):</Label>
                <Col sm={8} lang="fr" style={{ marginTop: '6px' }}>
                  {form?.buildingNameFrench ?? ''}
                </Col>
              </FormGroup>

              <FormGroup row>
                <Col sm={12}>
                  <Input id="french_building_name_translated" type="checkbox" name="buildingNameFrenchSync"
                    checked={Number(form?.buildingNameFrenchSync) === 1} onChange={handleChange} disabled={!form?.buildingNameFrench} />{' '}
                  <Label for="french_building_name_translated" >Translated Building Name</Label>
                </Col>
              </FormGroup>
            </>
          }
        </Col>
      </Row>

      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="municipality" sm={4}>Municipality/City: (required)</Label>
            <Col sm={8}>
              <CityInput id="municipality" name="municipality"
                aria-label="Municipality"
                placeholder="Enter to search a municipality or city"
                value={form?.municipalityId}
                innerName="municipalityId"
                required min={1}
                invalid={!!errors.municipalityId} onInvalid={handleInvalid}
                onConfirm={handleSelectMunicipality} />
              <InlineFeedback invalid={!!errors.municipalityId}>
                {errors.municipalityId}
              </InlineFeedback>
            </Col>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="province" sm={4}>Province: (required)</Label>
            <Col sm={8}>
              <Input type="select" id="province" name="provinceId"
                value={form?.provinceId ?? 0} onChange={handleChange}>
                <option value="0" disabled>Select a province</option>
                {provinces?.map(item =>
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                )}
              </Input>
            </Col>
          </FormGroup>
        </Col>
      </Row>

      <Row className="gx-5">
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="country" sm={4}>Country: (required)</Label>
            <Col sm={8}>
              <Input type="select" id="country" name="countryId"
                value={form?.countryId ?? 0} onChange={handleChange}>
                <option value="0" disabled>Select a country</option>
                {countries?.map(item =>
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                )}
              </Input>
            </Col>
          </FormGroup>
        </Col>
        <Col md={colWidth}>
          <FormGroup row>
            <Label for="postal_code" sm={4}>Postal Code: (required)</Label>
            <Col sm={8}>
              <PostalCodeInput id="postal_code" name="postalCode"
                aria-label="Postal code"
                placeholder="Enter to search a postal code"
                value={form?.postalCodeId}
                innerName="postalCode" onChange={handleChange}
                required
                pattern="^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$"
                invalid={!!errors.postalCode} onInvalid={handleInvalid}
                onConfirm={handleSelectPostalCode} />
              <InlineFeedback invalid={!!errors.postalCode}>
                {errors.postalCode}
              </InlineFeedback>
            </Col>
          </FormGroup>
        </Col>
      </Row>
    </section>
  );
};

export default AddressInfoPanel;
